// src/views/Doctors/Doctorsdetails.jsx
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  Stack,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import doctors from "./doctors-data";
import doctorBg from "assets/images/doctorbg.jpg";

// Components - adjust paths if needed
import BookAppointmentModel from "components/BookAppointmentModel";
import OurDoctors from "components/OurDoctors";
import Blog from "components/Blog";

const Doctorsdetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const doctor = doctors.find((d) => d.slug === slug);

  // Modal state for appointment
  const [openAppt, setOpenAppt] = useState(false);
  const openAppointment = () => setOpenAppt(true);
  const closeAppointment = () => setOpenAppt(false);

  if (!doctor) {
    return (
      <Box p={4}>
        <Typography variant="h5">Not Found</Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate("/our-doctor")}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Helmet for SEO */}
      <Helmet>
        <title>{doctor.meta?.title || doctor.name}</title>
        <meta name="description" content={doctor.meta?.description || doctor.short} />
        <meta property="og:title" content={doctor.meta?.title || doctor.name} />
        <meta property="og:description" content={doctor.meta?.description || doctor.short} />
        <meta property="og:image" content={doctor.photo} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Physician",
            name: doctor.name,
            image: doctor.photo,
            description: doctor.meta?.description || doctor.short,
            telephone: doctor.contact?.phone,
            address: { "@type": "PostalAddress", addressLocality: "Chennai" },
          })}
        </script>
      </Helmet>

      {/* Banner with background image */}
      <Box
        sx={{
          backgroundImage: `url(${doctorBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: { xs: 8, md: 25 },
          textAlign: "center",
          color: "text.primary",
        }}
      >
        <Typography variant="h2" fontWeight={600}>
          {doctor.name}
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={4} px={{ xs: 3, md: 10 }} py={6}>
        {/* Left profile */}
        <Grid item xs={12} md={4}>
  <Box
    sx={{
      border: "1px solid #e9eef0",
      borderRadius: 3,
      p: 3,
      textAlign: "center",
      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
      backgroundColor: "background.paper",
    }}
  >
    {/* Doctor Image */}
    <Box
      sx={{
        width: "100%",
        height: 350, // fixed height
        overflow: "hidden",
        borderRadius: 2,
      }}
    >
      <img
        src={doctor.photo}
        alt={doctor.name}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "0 -22px",
        }}
        loading="lazy"
      />
    </Box>

    {/* Doctor Info */}
    <Typography mt={2} fontWeight={700} fontSize="1.1rem" color="text.primary">
      {doctor.name}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
      {doctor.designation}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", mt: 0.5, mb: 2 }}
    >
      {doctor.hospital}
    </Typography>

    {/* Book Button */}
    <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              onClick={openAppointment} // opens the appointment modal
            >
              Book an Appointment
            </Button>
  </Box>
</Grid>



        {/* Right details */}
        <Grid item xs={12} md={8}>
  {/* Doctor Name */}
  <Typography
    variant="h4"
    fontWeight={700}
    gutterBottom
    sx={{ color: "text.primary" }}
  >
    {doctor.name}
  </Typography>

  {/* Designation + Qualification */}
  <Typography
    variant="h6"
    sx={{ fontWeight: 500, color: "text.secondary", mb: 3 }}
  >
    {doctor.designation}
    {doctor.qualifications ? ` | ${doctor.qualifications}` : ""}
  </Typography>

  {/* Bio */}
  <Typography
    variant="body1"
    sx={{ whiteSpace: "pre-line", color: "text.primary", lineHeight: 1.8 }}
  >
    {doctor.bio}
  </Typography>

  <Divider sx={{ my: 4 }} />

  {/* Surgeries Performed */}
  <Typography
    variant="h5"
    fontWeight={700}
    gutterBottom
    sx={{ color: "text.primary" }}
  >
     {doctor.Surgeries}
  </Typography>

  <Grid container spacing={2}>
    {doctor.highlights?.map((h, i) => (
      <Grid item xs={12} sm={6} key={i}>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Typography
            component="span"
            sx={{ color: "success.main", fontSize: "1.25rem", lineHeight: 1 }}
          >
            ✓
          </Typography>
          <Typography variant="body1" sx={{ color: "text.primary" }}>
            {h}
          </Typography>
        </Box>
      </Grid>
    ))}
  </Grid>

  <Divider sx={{ my: 4 }} />

  {/* OPD Section */}
  <Typography
    variant="h5"
    fontWeight={700}
    gutterBottom
    sx={{ color: "text.primary" }}
  >
    {doctor.opdex?.location}
  </Typography>
  
  <Typography
    variant="h5"
    fontWeight={700}
    gutterBottom
    sx={{ color: "text.primary" }}
  >
   {doctor.opd?.location}
  </Typography>

  <Box mt={1}>
    {doctor.opd?.timings?.map((t, i) => (
      <Typography
        key={i}
        variant="body1"
        sx={{ mb: 0.5, color: "text.primary" }}
      >
        <strong style={{ fontWeight: 600 }}>{t.days}</strong> –{" "}
        <span style={{ fontWeight: 500 }}>{t.time}</span>
      </Typography>
    ))}
  </Box>
  <Typography
    variant="h5"
    fontWeight={700}
    gutterBottom
    sx={{ color: "text.primary" }}
  >
   {doctor.opd2?.location}
  </Typography>

  <Box mt={1}>
    {doctor.opd2?.timings?.map((t, i) => (
      <Typography
        key={i}
        variant="body1"
        sx={{ mb: 0.5, color: "text.primary" }}
      >
        <strong style={{ fontWeight: 600 }}>{t.days}</strong> –{" "}
        <span style={{ fontWeight: 500 }}>{t.time}</span>
      </Typography>
    ))}
  </Box>
</Grid>

      </Grid>

 

      {/* Blog section */}
      <Box px={{ xs: 3, md: 10 }} pb={12}>
        <Blog />
      </Box>

      {/* Appointment modal */}
      {openAppt && (
        <BookAppointmentModel open={openAppt} onClose={closeAppointment} doctor={doctor} />
      )}
    </Box>
  );
};

export default Doctorsdetails;
